import { Button, Callout } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import * as React from 'react';

import { Text } from 'bones/components/atoms/Text';
import { Tooltip } from 'bones/components/atoms/Tooltip';
import { ExpressionBlock } from 'bones/components/molecules/QueryBuilder/ExpressionBlock';
import { useQueryBuilderContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilder';
import { useQueryBuilderSchemaContext } from 'bones/components/molecules/QueryBuilder/hooks/useQueryBuilderSchema';
import { createNewExpression } from 'bones/utils/queryBuilder/helpers/create';
import {
  DraftExpression,
  DraftSubExpression,
} from 'bones/utils/queryBuilder/types';

export interface SubExpressionBlockProps {
  duplicateSubExpression: () => void;
  subExpressionIdx: number;
  updateSubExpression: (
    subExpression: DraftSubExpression,
    validate?: boolean
  ) => void;
}

export const SubExpressionBlock: React.VFC<SubExpressionBlockProps> = ({
  duplicateSubExpression,
  subExpressionIdx,
  updateSubExpression,
}) => {
  const { copy, testIds, andNotDisabled, duplicateDisabled } =
    useQueryBuilderSchemaContext();
  const { topExpression } = useQueryBuilderContext();
  const subExpression = topExpression.expressions[subExpressionIdx];
  const { expressions } = subExpression;

  // istanbul ignore if
  if (!expressions.length) {
    throw new Error('sub-expression must have at least one expression');
  }

  const addExpression = () => {
    const lastExpression =
      subExpression.expressions[subExpression.expressions.length - 1];
    const { subject } = lastExpression.filters[0];
    updateSubExpression({
      ...subExpression,
      expressions: [...subExpression.expressions, createNewExpression(subject)],
    });
  };

  const updateExpression =
    (idx: number) => (expression: DraftExpression, validate?: boolean) => {
      const expressions = [...subExpression.expressions];
      if (expression.filters.length) {
        expressions.splice(idx, 1, expression);
      } else {
        expressions.splice(idx, 1);
      }

      updateSubExpression({ ...subExpression, expressions }, validate);
    };

  const removeExpression = (idx: number) => () => {
    const expressions = [...subExpression.expressions];
    if (subExpressionIdx || idx) {
      expressions.splice(idx, 1);
    } else {
      expressions.splice(idx, 1, createNewExpression());
    }

    updateSubExpression({ ...subExpression, expressions }, true);
  };

  return (
    <Callout
      className="sd-query-builder-sub-expression"
      data-testid={testIds.subExpressionBlock}
    >
      {expressions.map((subExpression, idx) => {
        const isLastElement = idx === expressions.length - 1;
        let conjunctionEl: JSX.Element | null = null;
        if (!isLastElement) {
          conjunctionEl = (
            <Text className="sd-query-builder-sub-expression-conjunction">
              {copy.body.conjunction.andNot}
            </Text>
          );
        }

        return (
          <React.Fragment key={subExpression.id}>
            <ExpressionBlock
              expressionIdx={idx}
              removeExpression={removeExpression(idx)}
              subExpressionIdx={subExpressionIdx}
              updateExpression={updateExpression(idx)}
            />
            {conjunctionEl}
          </React.Fragment>
        );
      })}
      <div className="sd-query-builder-sub-expression-actions">
        <div>
          {andNotDisabled ? null : (
            <Button
              data-testid={testIds.addExpressionButton}
              icon={IconNames.ADD}
              onClick={addExpression}
              text={copy.body.conjunction.andNot}
            />
          )}
        </div>
        <div>
          {duplicateDisabled ? null : (
            <Tooltip content={copy.body.expression.duplicate}>
              <Button
                data-testid={testIds.duplicateExpressionButton}
                icon={IconNames.DUPLICATE}
                minimal
                onClick={duplicateSubExpression}
              />
            </Tooltip>
          )}
        </div>
      </div>
    </Callout>
  );
};
